import { useMemo } from 'react';

import { Box, Text } from '@joinrepublic/dls-ui-kit';

import { useInxTickerQuery } from '@r-client/republic/data/offerings';
import {
  CardRail,
  SectionHeader,
  SeeAllTile,
} from '@r-client/republic/ui/offerings';
import { ASSET_TYPES } from '@r-client/shared/data/analytics';
import { isInitialQueryLoading } from '@r-client/shared/util/core';

import { useInvestmentOpportunitiesAnalytics } from '../../hooks/use-investment-opportunities-analytics';
import { CryptoAssetCard } from '../crypto-asset-card/crypto-asset-card';
import {
  EMPTY_MESSAGE,
  HEADING,
  SEE_ALL_HREF,
  SLIDE_WIDTH,
  SUBHEADING,
  TILE_ICON_SIZE,
  TILE_RADIUS,
} from './constants';
import { CryptoAssetsError } from './crypto-assets-error/crypto-assets-error';
import { CryptoRailSkeleton } from './crypto-rail-skeleton/crypto-rail-skeleton';
import { toCryptoAssetCards } from './to-crypto-asset-card-props';

/**
 * Popular crypto assets section of the /investment-opportunities page: a rail of
 * compact price tiles fed by the INX ticker, sharing its scroller with the
 * funded-ventures section.
 *
 * Unlike its funded-ventures sibling this is not an `observer` — there is no
 * MobX here. That section wraps one because `BaseQueryModel` only installs its
 * variables autorun once observed; react-query has no such requirement.
 */
export const CryptoAssets = () => {
  const { tickerData, isLoading, isError, isFetching, refetch } =
    useInxTickerQuery();
  const { assetCardClicked, seeAllClicked } =
    useInvestmentOpportunitiesAnalytics();

  const assets = useMemo(() => toCryptoAssetCards(tickerData), [tickerData]);

  // Both "See all" affordances — the header link and the rail's terminal tile —
  // lead to the same place, so they report the same event.
  const onSeeAllClick = () => seeAllClicked(ASSET_TYPES.CRYPTO);

  const renderContent = () => {
    if (isInitialQueryLoading({ data: tickerData, isLoading }))
      return <CryptoRailSkeleton />;

    if (isError) {
      // Wrapped rather than passed directly: React would hand the click event
      // to `refetch` as its `RefetchOptions`, and the promise would float.
      return (
        <CryptoAssetsError
          onRetry={() => void refetch()}
          isRetrying={isFetching}
        />
      );
    }

    if (!assets.length) {
      return (
        <Text size="body-md" variant="text-secondary">
          {EMPTY_MESSAGE}
        </Text>
      );
    }

    return (
      <CardRail ariaLabel={HEADING} slideWidth={SLIDE_WIDTH}>
        {assets.map(({ id, ...asset }) => (
          <CryptoAssetCard
            key={id}
            {...asset}
            // The ticker symbol is what identifies a crypto asset — lowercased
            // so it reads like every other slug on the page.
            onClick={() =>
              assetCardClicked(ASSET_TYPES.CRYPTO, asset.symbol.toLowerCase())
            }
          />
        ))}
        <SeeAllTile
          href={SEE_ALL_HREF}
          isExternal
          borderRadius={TILE_RADIUS}
          iconSize={TILE_ICON_SIZE}
          onClick={onSeeAllClick}
        />
      </CardRail>
    );
  };

  return (
    <Box as="section">
      <SectionHeader
        title={HEADING}
        seeAllHref={SEE_ALL_HREF}
        isExternal={true}
        onSeeAllClick={onSeeAllClick}
      />

      <Text
        mt="1"
        size={{ base: 'body-sm-md', sm: 'body-md' }}
        variant="text-secondary"
      >
        {SUBHEADING}
      </Text>

      <Box mt="6">{renderContent()}</Box>
    </Box>
  );
};
